/**
 * Social Media Manager - Direct Database Implementation
 * 
 * This component implements social media management using direct database access
 * to the same Supabase instance as the BGV app, rather than going through BGV's API.
 */

import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus, Filter, Search, RefreshCw, Calendar, Grid, List, Send, Loader2 } from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Badge } from '@/components/ui/badge'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { cn } from '@/lib/utils'

// Import direct implementation components
import PostCreateForm from '@/components/social/PostCreateForm'
import PostList from '@/components/social/PostList'
import PostSkeleton from '@/components/social/PostSkeleton'
import PostsError from '@/components/social/PostsError'
import QueueManager from '@/components/social/QueueManager'
import ProfileManager from '@/components/social/ProfileManager'

// Import direct database hooks
import {
  usePosts,
  useProfiles,
  useDeletePost,
  usePublishPost,
  useProcessScheduledPosts,
  useScheduledPosts,
  usePostStatusSubscription,
  type PostFilters,
  type SocialPost
} from '@/hooks/useSocialMedia'
import { POST_STATUS } from '@/constants/socialPostStatus'

export default function SocialMediaManagerDirect() {
  const navigate = useNavigate()
  const [showEditDialog, setShowEditDialog] = useState(false)
  const [selectedProfile, setSelectedProfile] = useState<string>('all')
  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [selectedPost, setSelectedPost] = useState<SocialPost | null>(null)
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid')
  const [isRefreshing, setIsRefreshing] = useState(false)

  // Direct database hooks
  const { data: profiles = [], isLoading: profilesLoading, refetch: refetchProfiles } = useProfiles()
  const { data: scheduledPosts = [] } = useScheduledPosts()
  
  // Enable real-time status updates
  const { isConnected, checkForUpdates } = usePostStatusSubscription()

  // Build filters for posts query
  const postFilters: PostFilters = {
    ...(statusFilter !== 'all' && { status: [statusFilter as any] }),
    ...(selectedProfile !== 'all' && { profiles: [selectedProfile] }),
    search: searchQuery
  }

  const { 
    data: posts = [], 
    isLoading: postsLoading,
    error: postsError,
    refetch: refetchPosts 
  } = usePosts(postFilters)

  // Mutations
  const deletePostMutation = useDeletePost()
  const publishPostMutation = usePublishPost()
  const processScheduledMutation = useProcessScheduledPosts()

  // Stats calculations
  const stats = {
    total: posts.length,
    draft: posts.filter(p => p.status === POST_STATUS.DRAFT).length,
    scheduled: posts.filter(p => p.status === POST_STATUS.SCHEDULED).length,
    published: posts.filter(p => p.status === POST_STATUS.POSTED).length,
    failed: posts.filter(p => p.status === POST_STATUS.ERROR).length
  }

  // Event handlers
  const handleCreatePost = () => {
    navigate('/marketing/social/create')
  }

  const handleEditPost = (post: SocialPost) => {
    setSelectedPost(post)
    setShowEditDialog(true)
  }

  const handleDeletePost = async (postId: string) => {
    if (window.confirm('Are you sure you want to delete this post? This action cannot be undone.')) {
      await deletePostMutation.mutateAsync(postId)
    }
  }

  const handleDuplicatePost = (post: SocialPost) => {
    // Store the post data in sessionStorage for the create page to pick up
    const duplicateData = {
      content: post.content,
      caption: post.caption || '',
      image_urls: post.image_urls || [],
      hashtags: post.hashtags || [],
      tags: post.tags || []
    }
    sessionStorage.setItem('duplicatePostData', JSON.stringify(duplicateData))
    navigate('/marketing/social/create')
  }

  const handlePublishPost = async (post: SocialPost) => {
    const testMode = !window.confirm('Are you sure you want to publish this post to Instagram? Click OK for production, Cancel for test mode.')
    await publishPostMutation.mutateAsync({ postId: post.id, testMode })
  }
  
  const handleRetryPost = async (post: SocialPost) => {
    // Retry is essentially publishing again
    const testMode = !window.confirm('Retry publishing this post to Instagram? Click OK for production, Cancel for test mode.')
    await publishPostMutation.mutateAsync({ postId: post.id, testMode })
  }
  
  const handleViewOnPlatform = (post: SocialPost) => {
    // This would need the Instagram post ID stored in platform_specific_data
    if (post.platform_specific_data?.instagram_post_id) {
      window.open(`https://www.instagram.com/p/${post.platform_specific_data.instagram_post_id}/`, '_blank')
    } else {
      alert('Instagram post ID not available. The post may not have been published successfully.')
    }
  }

  const handleProcessScheduled = async () => {
    await processScheduledMutation.mutateAsync()
  }

  const handleRefresh = async () => {
    setIsRefreshing(true)
    try {
      await Promise.all([
        refetchPosts(),
        refetchProfiles()
      ])
    } finally {
      setTimeout(() => setIsRefreshing(false), 500) // Minimum animation time
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold">Social Media Manager</h1>
          <p className="text-muted-foreground">
            Create, schedule, and publish posts directly to social media
          </p>
        </div>
        
        <div className="flex items-center gap-2">
          {/* Real-time connection indicator */}
          <div className={cn(
            "flex items-center gap-1.5 px-2 py-1 rounded-md text-xs",
            isConnected 
              ? "bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-400"
              : "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/20 dark:text-yellow-400"
          )}>
            <div className={cn(
              "h-2 w-2 rounded-full",
              isConnected 
                ? "bg-green-500 animate-pulse" 
                : "bg-yellow-500"
            )} />
            <span className="font-medium">
              {isConnected ? "Live" : "Connecting..."}
            </span>
          </div>
          
          <Button 
            variant="outline" 
            size="sm" 
            onClick={handleRefresh}
            disabled={isRefreshing}
          >
            <RefreshCw className={cn("h-4 w-4", isRefreshing && "animate-spin")} />
          </Button>
          
          <Button onClick={handleCreatePost}>
            <Plus className="h-4 w-4 mr-2" />
            Create Post
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-5">
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Total Posts</CardDescription>
            <CardTitle className="text-2xl">{stats.total}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Drafts</CardDescription>
            <CardTitle className="text-2xl">{stats.draft}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Scheduled</CardDescription>
            <CardTitle className="text-2xl">{stats.scheduled}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Published</CardDescription>
            <CardTitle className="text-2xl">{stats.published}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Failed</CardDescription>
            <CardTitle className="text-2xl">{stats.failed}</CardTitle>
          </CardHeader>
        </Card>
      </div>

      {/* Quick Actions */}
      {scheduledPosts.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-lg">Ready to Publish</CardTitle>
                <CardDescription>
                  {scheduledPosts.length} posts are due for publishing
                </CardDescription>
              </div>
              <Button 
                variant="outline" 
                onClick={handleProcessScheduled}
                disabled={processScheduledMutation.isPending}
              >
                {processScheduledMutation.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  'Process All'
                )}
              </Button>
            </div>
          </CardHeader>
        </Card>
      )}

      {/* Main Content Tabs */}
      <Tabs defaultValue="posts" className="space-y-4">
        <TabsList>
          <TabsTrigger value="posts">Posts</TabsTrigger>
          <TabsTrigger value="calendar">Calendar</TabsTrigger>
          <TabsTrigger value="queues">Queues</TabsTrigger>
          <TabsTrigger value="profiles">Profiles</TabsTrigger>
        </TabsList>

        <TabsContent value="posts" className="space-y-4">
          {/* Filters */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="flex-1">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Search posts..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                </div>
                
                <div className="flex gap-2">
                  <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger className="w-40">
                      <SelectValue placeholder="All Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="DRAFT">Draft</SelectItem>
                      <SelectItem value="SCHEDULED">Scheduled</SelectItem>
                      <SelectItem value="POSTED">Published</SelectItem>
                      <SelectItem value="ERROR">Failed</SelectItem>
                    </SelectContent>
                  </Select>

                  <Select value={selectedProfile} onValueChange={setSelectedProfile}>
                    <SelectTrigger className="w-40">
                      <SelectValue placeholder="All Profiles" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Profiles</SelectItem>
                      {profiles.map((profile) => (
                        <SelectItem key={profile.id} value={profile.id}>
                          {profile.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  <div className="flex gap-1 border rounded-md">
                    <Button
                      variant={viewMode === 'grid' ? 'secondary' : 'ghost'}
                      size="sm"
                      onClick={() => setViewMode('grid')}
                    >
                      <Grid className="h-4 w-4" />
                    </Button>
                    <Button
                      variant={viewMode === 'list' ? 'secondary' : 'ghost'}
                      size="sm"
                      onClick={() => setViewMode('list')}
                    >
                      <List className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            </CardHeader>
          </Card>

          {/* Posts List */}
          {postsError ? (
            <PostsError error={postsError} onRetry={handleRefresh} />
          ) : postsLoading || profilesLoading ? (
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {[...Array(6)].map((_, i) => (
                <PostSkeleton key={i} />
              ))}
            </div>
          ) : (
            <PostList
              posts={posts}
              profiles={profiles}
              onEdit={handleEditPost}
              onDelete={handleDeletePost}
              onDuplicate={handleDuplicatePost}
              onPublish={handlePublishPost}
              onRetry={handleRetryPost}
              onViewOnPlatform={handleViewOnPlatform}
            />
          )}
        </TabsContent>

        <TabsContent value="calendar">
          <Card>
            <CardHeader>
              <CardTitle>Content Calendar</CardTitle>
              <CardDescription>
                Visual calendar view of your scheduled posts
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-center py-12">
                <Calendar className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-lg font-semibold mb-2">Calendar View Coming Soon</h3>
                <p className="text-muted-foreground">
                  A visual calendar showing all your scheduled posts will be available in a future update.
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="queues">
          <QueueManager />
        </TabsContent>

        <TabsContent value="profiles">
          <ProfileManager />
        </TabsContent>
      </Tabs>

      {/* Edit Post Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Post</DialogTitle>
          </DialogHeader>
          <PostCreateForm
            initialData={selectedPost || undefined}
            onClose={() => {
              setShowEditDialog(false)
              setSelectedPost(null)
            }}
            onSuccess={() => {
              refetchPosts()
            }}
          />
        </DialogContent>
      </Dialog>
    </div>
  )
}